import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  generateInsurerAPIKeys,
  getInsurerProfile,
  insurerIntegrateWithWebhooks,
  setMIDCredentials,
  verifyMIDCredentials,
  setInsurerIntegrationType,
  InsurerIntegrationType,
} from '../../../api/requests';
import Page from '../../../components/PageWrapper';
import Button from '../../../components/form-control/Button';
import Input from '../../../components/form-control/Input';
import useUserAccount from '../../../hooks/userAccountHook';
import Loader from '../../../components/Loader';
import { CheckBoxInput } from '../../../components/form-control/CheckBoxInput';
import ToggleSwitch from '../../../components/form-control/ToggleSwitch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { showToast } from '../../../utils/miscFunctions';

type MIDVerifyStatus = 'OK' | 'INVALID' | 'UNREACHABLE' | null;

const INTEGRATION_OPTIONS: { value: InsurerIntegrationType; label: string; helper: string }[] = [
  { value: 'NONE', label: 'Brolly-managed only', helper: 'Policies for this insurer are managed inside Brolly only. No external system will be called.' },
  { value: 'NIC_MID_V1', label: 'NIC MID (V1)', helper: 'Uses your MID username + password to talk to NIC.' },
  { value: 'NIC_MID_V2', label: 'NIC MID (V2)', helper: 'Uses your MID API key to talk to NIC.' },
  { value: 'VANGUARD', label: 'Vanguard Assurance', helper: 'Uses Brolly’s shared Vanguard credentials by default. You can override with your own key + secret below.' },
];

const Developers = () => {
  const { getProfile } = useUserAccount();
  const [loading, setLoading] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<any>();
  const [verifying, setVerifying] = useState<boolean>(false);
  const [rotating, setRotating] = useState<'PRODUCTION' | 'SANDBOX' | null>(null);
  const [showRotateLiveConfirm, setShowRotateLiveConfirm] = useState<boolean>(false);
  const [integrationType, setIntegrationTypeLocal] = useState<InsurerIntegrationType>('NONE');
  const [savingIntegration, setSavingIntegration] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (profileData?.integrationType && profileData.integrationType !== integrationType) {
      setIntegrationTypeLocal(profileData.integrationType as InsurerIntegrationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData?.integrationType]);

  useQuery({ queryKey: ['getProfileDetails'], queryFn: () => getProfileDetails() })

  const getProfileDetails = async () => {
    setLoading(true);
    try {
      const response: any = await getInsurerProfile()
      setProfileData(response);
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not load the profile details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const integrateWithWebhooks = useMutation({
    mutationFn: (payload: any) => insurerIntegrateWithWebhooks(payload),
    onSuccess: (response: any) => {
      showToast.success(response?.message ?? 'Webhooks integrated successfully');
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    },
    onError: (error: any) => {
      showToast.error(error?.description);
    },
    onSettled: () => {
      setLoading(false);
    }
  })

  const rotateAPIKey = async (env: 'PRODUCTION' | 'SANDBOX') => {
    setRotating(env);
    try {
      const response: any = await generateInsurerAPIKeys(env);
      showToast.success(response?.message ?? `${env === 'PRODUCTION' ? 'Live' : 'Test'} API key rotated`);
      await getProfileDetails();
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not rotate the key. Please try again.');
    } finally {
      setRotating(null);
    }
  };

  const handleRotateLive = () => {
    setShowRotateLiveConfirm(true);
  };

  const confirmRotateLive = async () => {
    setShowRotateLiveConfirm(false);
    await rotateAPIKey('PRODUCTION');
  };

  const handleRotateTest = () => {
    rotateAPIKey('SANDBOX');
  };

  const handleSaveIntegrationType = async () => {
    if (savingIntegration) return;
    setSavingIntegration(true);
    try {
      await setInsurerIntegrationType(integrationType);
      showToast.success('Integration provider updated.');
      await getProfileDetails();
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not update the integration provider.');
    } finally {
      setSavingIntegration(false);
    }
  };

  const integrationHelper = INTEGRATION_OPTIONS.find(o => o.value === integrationType)?.helper ?? '';
  const integrationDirty = profileData?.integrationType && profileData.integrationType !== integrationType;

  const handleVerifyMID = async () => {
    if (verifying) return;
    setVerifying(true);
    try {
      const result = await verifyMIDCredentials();
      const status = result?.status;
      const message = result?.message ?? 'Verification complete';
      if (status === 'OK') {
        showToast.success(message);
      } else if (status === 'INVALID') {
        showToast.error(message);
      } else {
        showToast.error(message);
      }
      await getProfileDetails();
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not verify MID credentials.');
    } finally {
      setVerifying(false);
    }
  };

  const midStatus: MIDVerifyStatus = profileData?.developerSetting?.midLastVerifyStatus ?? null;
  const midVerifiedAt: string | null = profileData?.developerSetting?.midLastVerifiedAt ?? null;
  const testKeyRotatedAt: string | null = profileData?.developerSetting?.testKeyRotatedAt ?? null;
  const liveKeyRotatedAt: string | null = profileData?.developerSetting?.liveKeyRotatedAt ?? null;

  const formatWhen = (iso: string | null): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const midStatusPill = () => {
    if (midStatus === 'OK') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs bg-green-100 text-green-800">
          Verified{midVerifiedAt ? ` — ${formatWhen(midVerifiedAt)}` : ''}
        </span>
      );
    }
    if (midStatus === 'INVALID') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs bg-red-100 text-red-800">
          Invalid{midVerifiedAt ? ` — ${formatWhen(midVerifiedAt)}` : ''}
        </span>
      );
    }
    if (midStatus === 'UNREACHABLE') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs bg-amber-100 text-amber-800">
          NIC unreachable{midVerifiedAt ? ` — ${formatWhen(midVerifiedAt)}` : ''}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs bg-gray-100 text-gray-700">
        Never verified
      </span>
    );
  };

  const keyActivePill = (keyValue: string | null | undefined) => {
    if (keyValue && keyValue.length > 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs bg-green-100 text-green-800">
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs bg-gray-100 text-gray-700">
        Not generated
      </span>
    );
  };

  const copyTextToClipboard = async (text: string) => {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  const handleCopyClick = async (value: string) => {
    try {
      await copyTextToClipboard(value)
      showToast.success('Copied to clipboard');
    } catch (err) {
      showToast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Page title="Developers">
      <div className="min-h-full bg-white dark:bg-darkBg w-full p-5">
        {loading ? (
         
            <Loader message="Loading. Please wait..." brollyLoaderFixed />
         
        ) : (
          <>
            <div className="">
              <div className="">
                <h1 className="text-black dark:text-darkBg-mainText font-semibold">Developers</h1>
              </div>

              <span className="text-black dark:text-darkBg-subText text-sm">
                Update your developer/integration credentials here.
              </span>
            </div>

            <div className="border border-primary-border dark:border-darkBg-border rounded my-3">
              <div className="p-4">
                <h2 className="text-black dark:text-darkBg-mainText font-semibold">Integration Provider</h2>
                <p className="text-xs text-black dark:text-darkBg-subText mt-1">
                  Which external system Brolly routes this insurer&apos;s policy issuance and credential verification through.
                </p>
              </div>
              <hr />
              <div className="p-4 flex flex-col gap-3">
                <select
                  className="w-full max-w-md border border-primary-border dark:border-darkBg-border rounded px-3 py-2 bg-white dark:bg-darkBg text-black dark:text-darkBg-mainText"
                  value={integrationType}
                  onChange={(e) => setIntegrationTypeLocal(e.target.value as InsurerIntegrationType)}
                >
                  {INTEGRATION_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-black dark:text-darkBg-subText">{integrationHelper}</p>
                <div>
                  <Button
                    label={savingIntegration ? 'Saving…' : 'Save'}
                    type="button"
                    className="px-4 h-[2rem]"
                    bgColor="primary"
                    curved
                    onClick={handleSaveIntegrationType}
                    disabled={savingIntegration || !integrationDirty}
                    loading={savingIntegration}
                  />
                </div>
              </div>
              {integrationType === 'VANGUARD' && (
                <>
                  <hr />
                  <div className="p-4 flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-black dark:text-darkBg-mainText">Vanguard credentials (optional)</h3>
                    <p className="text-xs text-black dark:text-darkBg-subText">
                      Leave the API key blank to use Brolly&apos;s shared Vanguard account.
                      Provide values only if Vanguard has issued you your own key/secret.
                    </p>
                    <Formik
                      initialValues={{
                        vanguardApiKey: '',
                        vanguardApiSecret: '',
                      }}
                      onSubmit={async (values) => {
                        try {
                          await setMIDCredentials({
                            branchCode: profileData?.branchCode ?? '',
                            midApiKey: values.vanguardApiKey ?? '',
                            midPassword: profileData?.midPassword ?? '',
                            midReady: profileData?.ready ?? false,
                            midUsername: profileData?.midUsername ?? '',
                            nicRegistrationNumber: profileData?.nicRegistration ?? '',
                            apiSecret: values.vanguardApiSecret ?? '',
                          });
                          showToast.success('Vanguard credentials saved');
                          await getProfileDetails();
                        } catch (error: any) {
                          showToast.error(error?.description ?? 'Could not save Vanguard credentials.');
                        }
                      }}
                    >
                      {({ handleChange, handleSubmit, values }) => (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-md">
                          <Input
                            type="password"
                            name="vanguardApiKey"
                            label="Vanguard API Key (X-API-Key)"
                            showPasswordIcon
                            value={values.vanguardApiKey}
                            onChange={handleChange}
                          />
                          <Input
                            type="password"
                            name="vanguardApiSecret"
                            label="Vanguard API Secret (X-API-Secret)"
                            showPasswordIcon
                            value={values.vanguardApiSecret}
                            onChange={handleChange}
                          />
                          <div>
                            <Button label="Save Vanguard credentials" type="submit" className="px-4 h-[2rem]" bgColor="primary" curved />
                          </div>
                        </form>
                      )}
                    </Formik>
                  </div>
                </>
              )}
            </div>

            <div className="">
              <Formik
                initialValues={{
                  branchCode: profileData?.branchCode ?? '',
                  midApiKey: profileData?.midApiKey ?? '',
                  midPassword: profileData?.midPassword ?? '',
                  midReady: profileData?.ready ?? false,
                  midUsername: profileData?.midUsername ?? '',
                  nicRegistrationNumber: profileData?.nicRegistration ?? '',
                }}
                validateOnBlur
                validationSchema={Yup.object().shape({
                  branchCode: Yup.string().required('Branch code is required'),
                  nicRegistrationNumber: Yup.string().required('NIC registration number is required'),
                  midUsername: Yup.string().required('MID username is required'),
                  midPassword: Yup.string().required('MID password is required'),
                  midApiKey: Yup.string().required('MID API key is required'),
                })}
                onSubmit={async (values) => {
                  try {
                    const res: any = await setMIDCredentials(values);
                    showToast.success(res?.message ?? 'MID credentials updated successfully');
                  } catch (error: any) {
                    showToast.error(error?.description);
                  } finally {
                    setLoading(false);
                  }
                }}>
                {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, errors, touched }) => (
                  <form onSubmit={handleSubmit}>
                    <>
                      <div className="border border-primary-border dark:border-darkBg-border rounded my-3">
                        <div className="p-4 flex items-center gap-5 flex-wrap">
                          <h2 className=" text-black dark:text-darkBg-mainText font-semibold">Interface with MID</h2>
                          <ToggleSwitch
                            label={'Enable interface with MID'}
                            name={'midReady'}
                            checked={values.midReady}
                            onChange={(value) => { setFieldValue('midReady', value) }}
                          />
                          {midStatusPill()}
                        </div>
                        <hr />

                        {values.midReady && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center justify-center p-4">
                              <Input
                                type="text"
                                name="branchCode"
                                label="Branch Code"
                                value={values.branchCode}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.branchCode ? errors.branchCode : undefined}
                                helperText={touched.branchCode ? errors.branchCode : undefined}
                              />
                              <Input
                                type="text"
                                name="nicRegistrationNumber"
                                label="NIC Registration Number"
                                value={values.nicRegistrationNumber}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.nicRegistrationNumber ? errors.nicRegistrationNumber : undefined}
                                helperText={touched.nicRegistrationNumber ? errors.nicRegistrationNumber : undefined}
                              />
                              <Input
                                type="text"
                                name="midUsername"
                                label="MID Username"
                                value={values.midUsername}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.midUsername ? errors.midUsername : undefined}
                                helperText={touched.midUsername ? errors.midUsername : undefined}
                              />
                              <Input
                                type="password"
                                name="midPassword"
                                label="MID Password"
                                showPasswordIcon
                                value={values.midPassword}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.midPassword ? errors.midPassword : undefined}
                                helperText={touched.midPassword ? errors.midPassword : undefined}
                              />
                              <Input
                                type="password"
                                name="midApiKey"
                                label="MID API Key"
                                showPasswordIcon
                                value={values.midApiKey}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.midApiKey ? errors.midApiKey : undefined}
                                helperText={touched.midApiKey ? errors.midApiKey : undefined}
                              />
                            </div>
                            <div className="flex justify-start p-4 gap-3">
                              <Button
                                label="Save"
                                type="submit"
                                className="px-2 h-[2rem]"
                                bgColor="primary"
                                size="lg"
                                curved
                                disabled={loading}
                                loading={loading}
                              />
                              <Button
                                label={verifying ? 'Verifying…' : 'Verify'}
                                type="button"
                                className="px-2 h-[2rem]"
                                bgColor="secondary"
                                size="lg"
                                curved
                                onClick={handleVerifyMID}
                                disabled={
                                  verifying ||
                                  !values.midReady ||
                                  !values.branchCode ||
                                  !values.nicRegistrationNumber ||
                                  !values.midUsername ||
                                  !values.midPassword ||
                                  !values.midApiKey
                                }
                                loading={verifying}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      <div className="border border-primary-border dark:border-darkBg-border rounded my-3">
                        <div className="p-4">
                          <h2 className=" text-black dark:text-darkBg-mainText font-semibold">API Keys</h2>
                          <p className="text-xs text-black dark:text-darkBg-subText mt-1">
                            Brolly-issued keys. Rotate to revoke and reissue. You cannot paste a custom value.
                          </p>
                        </div>

                        <hr />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start p-4">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">Test API Key</span>
                              {keyActivePill(profileData?.developerSetting?.testKey)}
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                value={profileData?.developerSetting?.testKey ?? ''}
                                onClick={() =>
                                  handleCopyClick(profileData?.developerSetting?.testKey)
                                }
                                readOnly
                                className="!bg-[#D1D5DB] cursor-pointer"
                              />
                              <Button
                                label="Copy"
                                bgColor="secondary"
                                curved
                                onClick={() => handleCopyClick(profileData?.developerSetting?.testKey)}
                                disabled={!profileData?.developerSetting?.testKey}
                                className="w-fit px-3 h-10 cursor-pointer"
                              />
                              <Button
                                label={rotating === 'SANDBOX' ? 'Rotating…' : 'Rotate'}
                                bgColor="primary"
                                curved
                                onClick={handleRotateTest}
                                disabled={rotating !== null}
                                loading={rotating === 'SANDBOX'}
                                className="w-fit px-5 h-10 cursor-pointer"
                              />
                            </div>
                            {testKeyRotatedAt && (
                              <span className="text-xs text-black dark:text-darkBg-subText">
                                Last rotated: {formatWhen(testKeyRotatedAt)}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">Live API Key</span>
                              {keyActivePill(profileData?.developerSetting?.liveKey)}
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                value={profileData?.developerSetting?.liveKey ?? ''}
                                readOnly
                                onClick={() =>
                                  handleCopyClick(profileData?.developerSetting?.liveKey)
                                }
                                className="!bg-[#D1D5DB] cursor-pointer"
                              />
                              <Button
                                label="Copy"
                                bgColor="secondary"
                                curved
                                onClick={() => handleCopyClick(profileData?.developerSetting?.liveKey)}
                                disabled={!profileData?.developerSetting?.liveKey}
                                className="w-fit px-3 h-10 cursor-pointer"
                              />
                              <Button
                                label={rotating === 'PRODUCTION' ? 'Rotating…' : 'Rotate'}
                                bgColor="primary"
                                curved
                                onClick={handleRotateLive}
                                disabled={rotating !== null}
                                loading={rotating === 'PRODUCTION'}
                                className="w-fit px-5 h-10"
                              />
                            </div>
                            {liveKeyRotatedAt && (
                              <span className="text-xs text-black dark:text-darkBg-subText">
                                Last rotated: {formatWhen(liveKeyRotatedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  </form>
                )}
              </Formik>

              <Formik
                initialValues={{
                  enable: profileData?.developerSetting?.enable ?? false,
                  enableForClaims: profileData?.developerSetting?.enableForClaims ?? false,
                  enableForPayouts: profileData?.developerSetting?.enableForPayouts ?? false,
                  enableForPolicies: profileData?.developerSetting?.enableForPolicies ?? false,
                  enableForSales: profileData?.developerSetting?.enableForSales ?? false,
                  webhookUri: profileData?.developerSetting?.webhookUri ?? null
                }}
                validateOnBlur
                validationSchema={Yup.object().shape({
                  webhookUri: Yup.string()
                    .url('Invalid URL')
                    .nullable()
                    .when('enable', {
                      is: true,
                      then: (schema) => schema.required('Webhook URI is required when webhooks are enabled')
                    }),
                })}
                onSubmit={async (values) => {
                  const payload = {
                    enable: values.enable,
                    enableForClaims: values.enableForClaims,
                    enableForPayouts: values.enableForPayouts,
                    enableForPolicies: values.enableForPolicies,
                    enableForSales: values.enableForSales,
                    webhookUri: values.webhookUri
                  }
                  integrateWithWebhooks.mutate(payload)
                }}>
                {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, errors, touched }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="border border-primary-border dark:border-darkBg-border rounded ">
                      <div className="p-4 flex items-center gap-5">
                        <h2 className=" text-black dark:text-darkBg-mainText font-semibold">Enable Webhooks</h2>
                        <>
                          <ToggleSwitch
                            label={'Enable callbacks and webhooks'}
                            name={'enable'}
                            checked={values.enable}
                            onChange={(value: boolean) => {
                              setFieldValue('enable', value);
                            }}
                          />
                        </>
                      </div>
                      <hr />
                      {values.enable && (
                        <>
                          <div className="grid grid-cols-1 gap-6 p-4">
                            <>
                              <CheckBoxInput
                                label={'Enable Webhook for claims'}
                                name={'enableForClaims'}
                                checked={values.enableForClaims}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForClaims', value);
                                }}
                              />
                              <CheckBoxInput
                                label={'Enable Webhook for payouts'}
                                name={'enableForPayouts'}
                                checked={values.enableForPayouts}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForPayouts', value);
                                }}
                              />
                              <CheckBoxInput
                                label={'Enable Webhook for policies'}
                                name={'enableForPolicies'}
                                checked={values.enableForPolicies}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForPolicies', value);
                                }}
                              />
                              <CheckBoxInput
                                label={'Enable Webhook for sales'}
                                name={'enableForSales'}
                                checked={values.enableForSales}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForSales', value);
                                }}
                              />
                            </>
                            <>
                              <Input
                                type="url"
                                name="webhookUri"
                                label="Webhook URL"
                                value={values.webhookUri}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="callback URL"
                                error={touched.webhookUri ? errors.webhookUri : undefined}
                                helperText={touched.webhookUri ? errors.webhookUri : undefined}
                              />
                            </>
                          </div>
                          <div className="flex justify-start p-4">
                            <Button
                              label="Save"
                              type="submit"
                              className="px-2 h-[2rem]"
                              bgColor="primary"
                              size="lg"
                              curved
                              disabled={loading}
                              loading={loading}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </>
        )}
      </div>
      {showRotateLiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded bg-white dark:bg-darkBg p-5 shadow-lg">
            <h3 className="text-lg font-semibold text-black dark:text-darkBg-mainText mb-2">
              Rotate Live API key?
            </h3>
            <p className="text-sm text-black dark:text-darkBg-subText mb-4">
              This will revoke the current live key. Any external system using it will start failing until updated with the new value.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                label="Cancel"
                bgColor="secondary"
                curved
                onClick={() => setShowRotateLiveConfirm(false)}
                className="px-4 h-9"
              />
              <Button
                label="Rotate"
                bgColor="primary"
                curved
                onClick={confirmRotateLive}
                className="px-4 h-9"
              />
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default Developers;
